import React, { useState, useEffect } from 'react';
import { calcBatchBody } from '@carboncare/schemas';
import { preflight } from '../services/api/schemaPreflight';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PlayArrow as ProcessIcon,
  Edit as EditIcon,
  PlayArrow as CalculateIcon,
} from '@mui/icons-material';
import { excelApi } from '../services/api/excelApi';
import { InlineColumnMapper } from '../components/excel/InlineColumnMapper';
import DelimiterSelector from '../components/excel/DelimiterSelector';
import { CsvDelimiter, normalizeCsvDelimiter } from '../utils/csvDelimiter';
import { ManualEntry } from '../components/excel/ManualFieldEntry';
import { ExcelData, ColumnMappingData, SchemaData } from './ExcelUploadPage';
import { SchemaSaveDialog } from '../components/excel/SchemaSaveDialog';
import { useAuthStore } from '../store/authStore';
import { fileService, FileInfo } from '../services/fileService';
import PageHeader from '../components/common/PageHeader';
import { apiClient } from '../services/api/authApi';
import { AccountType } from '../types/auth';
import { getUserTypeLabel, formatFileSize, makeHandleSort } from './_shared/fileUtils';
import {
  SortableTableHeaderCell,
  FilesTableContainer,
  FilesTableFooter,
  FilesSearchFilterCard,
  FileNameCell,
  DeleteIconButton,
} from './_shared/FilesTableShared';

// FileInfo interface is now imported from fileService

const UploadedFilesPage: React.FC = () => {
  const { user } = useAuthStore();
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterCompanyId, setFilterCompanyId] = useState<number | ''>('');
  const [filterCompanyName, setFilterCompanyName] = useState<string>('');
  const [success, setSuccess] = useState<string | null>(null);

  // Sorting
  const [sortBy, setSortBy] = useState<'uploadedAt' | 'fileName'>('uploadedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalFiles, setTotalFiles] = useState(0);

  // Edit Mapping State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [excelData, setExcelData] = useState<ExcelData | null>(null);
  // Held so a delimiter change can re-preview without re-downloading the blob.
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMappingData>({});
  const [manualEntries, setManualEntries] = useState<ManualEntry[]>([]);
  const [legCount, setLegCount] = useState(1);
  const [isMappingValid, setIsMappingValid] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [processingMapping, setProcessingMapping] = useState(false);

  // Schema state
  const [savedSchemas, setSavedSchemas] = useState<SchemaData[]>([]);
  const [loadedSchema, setLoadedSchema] = useState<SchemaData | undefined>(undefined);
  const [originalMappings, setOriginalMappings] = useState<ColumnMappingData>({});
  const [schemaSaveDialogOpen, setSchemaSaveDialogOpen] = useState(false);
  const [pendingCalcMode, setPendingCalcMode] = useState<'queue' | 'immediate' | null>(null);

  const isMasterAdmin = user?.accountType === AccountType.MasterAdmin || user?.accountType === AccountType.CarbonCareAdmin;
  const canRunCalculation = !!user?.accountType && user.accountType >= AccountType.CarbonCareAdmin;
  // Uploaded files: CarbonCareAdmin+ can delete any; a Licensee Admin only their own upload. Users cannot.
  const canDeleteUploaded = (file: FileInfo): boolean => {
    if (!user?.accountType) return false;
    if (user.accountType >= AccountType.CarbonCareAdmin) return true;
    return user.accountType === AccountType.LicenseeAdmin && file.uploadedBy?.id === user.id;
  };

  const location = useLocation();
  const navigate = useNavigate();

  // Consume one-shot toast passed via router state (e.g. after LA upload redirect).
  useEffect(() => {
    const toast = (location.state as { uploadedToast?: string } | null)?.uploadedToast;
    if (!toast) return;
    setSuccess(toast);
    navigate(location.pathname, { replace: true, state: {} });
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = makeHandleSort(sortBy, setSortBy, setSortDirection, setPage);

  const fetchFiles = async (
    currentPage = page,
    currentLimit = rowsPerPage,
    search = searchTerm,
    companyName = filterCompanyName,
    currentSortBy = sortBy,
    currentSortDirection = sortDirection,
  ) => {
    try {
      setLoading(true);
      const result = await fileService.getUploadedFiles({
        page: currentPage + 1,
        limit: currentLimit,
        search: search || undefined,
        companyName: companyName || undefined,
        sortBy: currentSortBy,
        sortDirection: currentSortDirection,
      });
      setFiles(result.data);
      setTotalFiles(result.pagination.total);
    } catch (error) {
      console.error('Error fetching uploaded files:', error);
      setError('Failed to load uploaded files');
    } finally {
      setLoading(false);
    }
  };



  // Single effect for fetching — debounces search, immediate for page/filter changes
  const prevSearch = React.useRef(searchTerm);
  const prevCompany = React.useRef(filterCompanyName);

  useEffect(() => {
    const searchChanged = prevSearch.current !== searchTerm;
    const companyChanged = prevCompany.current !== filterCompanyName;
    prevSearch.current = searchTerm;
    prevCompany.current = filterCompanyName;

    if (searchChanged) {
      // Debounce search input
      const timer = setTimeout(() => {
        setPage(0);
        fetchFiles(0, rowsPerPage, searchTerm, filterCompanyName);
      }, 600);
      return () => clearTimeout(timer);
    }

    if (companyChanged) {
      setPage(0);
      fetchFiles(0, rowsPerPage, searchTerm, filterCompanyName);
      return;
    }

    // page/rowsPerPage/user/sort change — fetch immediately
    fetchFiles();
  }, [user, page, rowsPerPage, searchTerm, filterCompanyName, sortBy, sortDirection]);

  // Fetch schemas for schema save dialog
  useEffect(() => {
    excelApi.getSchemas().then(schemas => setSavedSchemas(schemas)).catch(() => {});
  }, []);

  const mappingsEqual = (a: ColumnMappingData, b: ColumnMappingData) => {
    const ka = Object.keys(a).sort((x, y) => x.localeCompare(y));
    const kb = Object.keys(b).sort((x, y) => x.localeCompare(y));
    if (ka.length !== kb.length) return false;
    return ka.every((k, i) => k === kb[i] && a[k] === b[k]);
  };

  const handleSchemaSave = async (schemaName: string, settings: SchemaData['settings'], existingSchemaId?: string) => {
    const mergedMappings = { ...columnMappings };
    manualEntries.forEach(entry => {
      if (entry.fieldId && entry.value) {
        mergedMappings[`MANUAL:${entry.value}`] = entry.fieldId;
      }
    });

    const schemaData: SchemaData = {
      name: schemaName,
      mappings: mergedMappings,
      legCount,
      mappingType: 'inline',
      settings,
    };

    if (existingSchemaId) {
      await excelApi.updateSchema(existingSchemaId, schemaData);
    } else {
      await excelApi.createSchema(schemaData);
    }
    // Refresh schemas
    const schemas = await excelApi.getSchemas();
    setSavedSchemas(schemas);
  };

  const handleDownload = async (file: FileInfo) => {
    try {
      await fileService.downloadFile(file.id);
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download file');
    }
  };

  const handleDelete = async (file: FileInfo) => {
    try {
      await fileService.deleteFile(file.id);
      setDeleteDialogOpen(false);
      setSelectedFile(null);
      fetchFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      setError('Failed to delete file');
    }
  };

  const [sendingForCalc, setSendingForCalc] = useState(false);

  const handleSendForCalculation = async (file: FileInfo, mode: 'queue' | 'immediate' = 'queue') => {
    try {
      setSendingForCalc(true);

      const batchBody = { fileId: file.id, mode };
      preflight(calcBatchBody, batchBody);
      const response = await apiClient.post('/xml/calc/batch', batchBody);

      if (response.data.success) {
        setProcessDialogOpen(false);
        setSelectedFile(null);
        setSuccess(mode === 'queue' ? 'File added to calculation queue!' : 'File sent for calculation!');
      } else {
        throw new Error(response.data.message || 'Failed to start calculation');
      }
    } catch (error: any) {
      console.error('Error starting calculation:', error);
      setError(error.message || 'Failed to start calculation');
      setProcessDialogOpen(false);
      setSelectedFile(null);
    } finally {
      setSendingForCalc(false);
    }
  };

  const generateAutoMappings = (headers: string[], fieldDefs: any): ColumnMappingData => {
    const mappings: ColumnMappingData = {};
    const normalizedHeaders = headers.map(h => ({
      original: h,
      normalized: h.toLowerCase().replaceAll(/[^a-z0-9]/g, '')
    }));

    // Prepare fields
    const shipmentFields = fieldDefs.shipmentFields || [];
    const legFields = fieldDefs.legFields || [];
    
    // Helper to find match
    const findMatch = (fields: any[], suffix = '') => {
      fields.forEach(field => {
        const fieldName = field.name.toLowerCase().replaceAll(/[^a-z0-9]/g, '');
        const fieldId = field.id + suffix;

        // Exact normalized match
        let match: typeof normalizedHeaders[number] | undefined;
        for (const h of normalizedHeaders) {
          if (h.normalized === fieldName) {
            match = h;
            break;
          }
        }
        if (match && !mappings[match.original]) {
          mappings[match.original] = fieldId;
        }
      });
    };

    findMatch(shipmentFields);
    findMatch(legFields, '_leg1'); // Assume leg 1 for auto-mapping

    return mappings;
  };

  const handleEditMapping = async (file: FileInfo) => {
    setSelectedFile(file);
    setLoadingPreview(true);
    setEditDialogOpen(true);
    setError(null);
    setSuccess(null);

    try {
      const blob = await fileService.getFileBlob(file.id);
      
      const fileObj = new File([blob], file.originalName, {
        type: file.originalName.endsWith('.csv') 
          ? 'text/csv' 
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // A delimiter the user pinned earlier beats re-detection, which cannot know it.
      const storedDelimiter = normalizeCsvDelimiter(file.settings?.delimiter);
      const data = await excelApi.uploadFileForPreview(fileObj, storedDelimiter);
      setPreviewFile(fileObj);

      let initialMappings: ColumnMappingData = {};
      let initialManualEntries: ManualEntry[] = [];
      let initialLegCount = 1;

      // Check if file has existing mappings
      if (file.mappings && Object.keys(file.mappings).length > 0) {
        console.log('Using existing mappings:', file.mappings);
        
        Object.entries(file.mappings).forEach(([key, value]) => {
          if (key.startsWith('MANUAL:')) {
            const manualValue = key.replace('MANUAL:', '');
            initialManualEntries.push({
              fieldId: value,
              value: manualValue
            });
          } else {
            initialMappings[key] = value;
          }
        });

        // Infer leg count
        const legNumbers = Object.values(initialMappings)
          .map(fieldId => {
            const match = /_leg(\d+)$/.exec(fieldId);
            return match ? Number.parseInt(match[1], 10) : 0;
          })
          .filter(num => num > 0);
        
        if (legNumbers.length > 0) {
           initialLegCount = Math.max(...legNumbers);
        }
      } else {
        // Auto-map columns
        const fieldDefs = await excelApi.getFieldDefinitions(1);
        initialMappings = generateAutoMappings(data.headers, fieldDefs);
      }

      setExcelData(data);
      setColumnMappings(initialMappings);
      setOriginalMappings(initialMappings);
      setManualEntries(initialManualEntries);
      setLegCount(initialLegCount);

      // Try to find the schema that matches these mappings
      const matchingSchema = savedSchemas.find(s =>
        s.mappings && mappingsEqual(s.mappings, initialMappings)
      );
      setLoadedSchema(matchingSchema);
    } catch (error: any) {
      console.error('Error fetching file preview:', error);
      setError('Failed to load file for editing. ' + (error.message || ''));
      setEditDialogOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Re-reads the file through the server parser with a hand-picked delimiter. The
  // columns are re-cut, so mappings made against the previous parse are dropped.
  const handleDelimiterChange = async (delimiter: CsvDelimiter) => {
    if (!previewFile) return;
    setLoadingPreview(true);
    try {
      const data = await excelApi.uploadFileForPreview(previewFile, delimiter);
      setExcelData(data);
      setColumnMappings({});
      setOriginalMappings({});
      setManualEntries([]);
      setIsMappingValid(false);
    } catch (err: any) {
      setError('Failed to re-read the file with that delimiter. ' + (err.message || ''));
    } finally {
      setLoadingPreview(false);
    }
  };

  // Intercept calculate button — check if schema needs saving first
  const handleCalculateFromEdit = (mode: 'queue' | 'immediate') => {
    const mappingsChanged = !mappingsEqual(columnMappings, originalMappings);
    if (mappingsChanged) {
      setPendingCalcMode(mode);
      setSchemaSaveDialogOpen(true);
    } else {
      executeProcessWithMapping(mode);
    }
  };

  const executeProcessWithMapping = async (mode: 'queue' | 'immediate') => {
    if (!selectedFile || !excelData) return;

    try {
      setProcessingMapping(true);

      // First update the file's mappings in the DB
      const mergedMappings = { ...columnMappings };
      manualEntries.forEach(entry => {
        if (entry.fieldId && entry.value) {
          mergedMappings[`MANUAL:${entry.value}`] = entry.fieldId;
        }
      });

      // Send for calculation via batch endpoint with the updated mappings
      // Update mappings on the file first, then trigger batch
      await apiClient.put(`/excel/files/${selectedFile.id}/mappings`, {
        mappings: mergedMappings,
        settings: {
          ...(loadedSchema?.settings || { hasHeader: true, useStreetDistances: true, standard: 'ISO' }),
          // Sent even when unchanged; the server re-parses the stored file only if it differs.
          ...(excelData.delimiter ? { delimiter: excelData.delimiter } : {}),
        },
      });

      const mappingsBatchBody = { fileId: selectedFile.id, mode };
      preflight(calcBatchBody, mappingsBatchBody);
      const response = await apiClient.post('/xml/calc/batch', mappingsBatchBody);

      if (response.data.success) {
        setEditDialogOpen(false);
        setExcelData(null);
        setSuccess(mode === 'queue' ? 'File added to calculation queue!' : 'File sent for calculation!');
        setSelectedFile(null);
      }
    } catch (error: any) {
      console.error('Error starting calculation:', error);
      setError(error.message || 'Failed to start calculation');
    } finally {
      setProcessingMapping(false);
    }
  };

  const handleSchemaDialogSaveAndCalc = async (
    schemaName: string,
    settings: SchemaData['settings'],
    existingSchemaId?: string
  ) => {
    setSchemaSaveDialogOpen(false);
    try {
      await handleSchemaSave(schemaName, settings, existingSchemaId);
    } catch (e: any) {
      setError(e.message || 'Failed to save schema.');
      setPendingCalcMode(null);
      return;
    }
    if (pendingCalcMode) executeProcessWithMapping(pendingCalcMode);
    setPendingCalcMode(null);
  };

  const handleSchemaDialogSkip = (_dialogSettings: SchemaData['settings']) => {
    setSchemaSaveDialogOpen(false);
    if (pendingCalcMode) executeProcessWithMapping(pendingCalcMode);
    setPendingCalcMode(null);
  };


  return (
    <Box>
      <PageHeader description="Files you have uploaded, with their processing status." />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <FilesSearchFilterCard
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        isMasterAdmin={isMasterAdmin}
        filterCompanyId={filterCompanyId}
        onFilterCompanyChange={(id, name) => { setFilterCompanyId(id); setFilterCompanyName(name); }}
        total={totalFiles}
      />

      {/* Files Table */}
      <FilesTableContainer loading={loading}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTableHeaderCell
                columnKey="fileName"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
                defaultDirection="asc"
              >
                File Name
              </SortableTableHeaderCell>
              <TableCell>Size</TableCell>
              {user?.accountType && user?.accountType >= AccountType.CarbonCareAdmin && <TableCell>Licensee</TableCell>}
              <TableCell>Uploaded By</TableCell>
              <SortableTableHeaderCell
                columnKey="uploadedAt"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
                defaultDirection="desc"
              >
                Upload Date
              </SortableTableHeaderCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.id}>
                <FileNameCell name={file.originalName} subtitle={file.fileName} />
                <TableCell>{formatFileSize(file.fileSize)}</TableCell>
                {user?.accountType && user?.accountType >= AccountType.CarbonCareAdmin && (
                  <TableCell>{file.companyName}</TableCell>
                )}
                <TableCell>
                  <Box>
                    <Typography variant="body2">{file.uploadedBy.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getUserTypeLabel(file.userType)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {file.uploadedAt.toLocaleDateString()} {file.uploadedAt.toLocaleTimeString()}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleDownload(file)}
                    title="Download"
                  >
                    <DownloadIcon />
                  </IconButton>
                  {file.status === 'uploaded' && canRunCalculation && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedFile(file);
                          setProcessDialogOpen(true);
                        }}
                        title="Send for Calculation (Default Schema)"
                        color="success"
                      >
                        <ProcessIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditMapping(file)}
                        title="Edit Schema Mapping"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </>
                  )}
                  {canDeleteUploaded(file) && (
                    <DeleteIconButton
                      onClick={() => {
                        setSelectedFile(file);
                        setDeleteDialogOpen(true);
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </FilesTableContainer>

      <FilesTableFooter
        total={totalFiles}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        loading={loading}
        filesLength={files.length}
        emptyPrimaryText="No files found"
        emptySecondaryText="No files have been uploaded yet."
        deleteDialogOpen={deleteDialogOpen}
        onDeleteClose={() => setDeleteDialogOpen(false)}
        onDeleteConfirm={() => selectedFile && handleDelete(selectedFile)}
        deleteTitle="Delete File"
        deleteMessage={`Are you sure you want to delete "${selectedFile?.originalName}"? This action cannot be undone.`}
      />

      {/* Send for Calculation Dialog */}
      <Dialog open={processDialogOpen} onClose={() => !sendingForCalc && setProcessDialogOpen(false)}>
        <DialogTitle>Send for Calculation</DialogTitle>
        <DialogContent>
          <Typography>
            Send "{selectedFile?.originalName}" for carbon calculation?
          </Typography>
          {sendingForCalc && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessDialogOpen(false)} disabled={sendingForCalc}>
            Cancel
          </Button>
          <Button
            onClick={() => selectedFile && handleSendForCalculation(selectedFile, 'queue')}
            color="primary"
            variant="outlined"
            disabled={sendingForCalc}
          >
            Add to Queue
          </Button>
          <Button
            onClick={() => selectedFile && handleSendForCalculation(selectedFile, 'immediate')}
            color="primary"
            variant="contained"
            disabled={sendingForCalc}
          >
            Calculate Now
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Mapping Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          Edit Mapping: {selectedFile?.originalName}
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            if (loadingPreview) return (
            <Box display="flex" justifyContent="center" alignItems="center" height="300px">
              <LinearProgress sx={{ width: '50%' }} />
              <Typography sx={{ ml: 2 }}>Loading file data...</Typography>
            </Box>
            );
            if (excelData) return (
             <Box>
               {excelData.delimiter && (
                 <Box sx={{ mb: 2 }}>
                   <DelimiterSelector
                     delimiter={excelData.delimiter}
                     onChange={handleDelimiterChange}
                     disabled={loadingPreview}
                   />
                 </Box>
               )}
               <InlineColumnMapper
                  excelData={excelData}
                  legCount={legCount}
                  existingMappings={columnMappings}
                  existingManualEntries={manualEntries}
                  onMappingChange={(mappings, entries) => {
                    setColumnMappings(mappings);
                    setManualEntries(entries);
                  }}
                  onValidationChange={setIsMappingValid}
                  onChangeLegCount={() => {
                    setLegCount(prev => prev >= 3 ? 1 : prev + 1);
                  }}
                  hasHeader={loadedSchema?.settings?.hasHeader}
               />
               <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                 Click "Change leg count" above to cycle between 1, 2, and 3 legs.
               </Typography>
             </Box>
            );
            return (
            <Alert severity="error">Failed to load file data.</Alert>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={processingMapping} variant="outlined">Cancel</Button>
          <Button
            variant="outlined"
            onClick={() => handleCalculateFromEdit('queue')}
            disabled={loadingPreview || !excelData || !isMappingValid || processingMapping}
          >
            Add to Queue
          </Button>
          <Button
            variant="contained"
            onClick={() => handleCalculateFromEdit('immediate')}
            disabled={loadingPreview || !excelData || !isMappingValid || processingMapping}
            startIcon={<CalculateIcon />}
          >
            Calculate Now
          </Button>
        </DialogActions>
        {processingMapping && <LinearProgress />}
      </Dialog>

      {/* Schema Save Dialog */}
      <SchemaSaveDialog
        open={schemaSaveDialogOpen}
        onClose={() => { setSchemaSaveDialogOpen(false); setPendingCalcMode(null); }}
        onSaveAndProcess={handleSchemaDialogSaveAndCalc}
        onProcessWithoutSaving={handleSchemaDialogSkip}
        schemas={savedSchemas}
        loadedSchema={loadedSchema}
        currentMappings={columnMappings}
        headers={excelData?.headers}
      />
    </Box>
  );
};

export default UploadedFilesPage;